import { useCallback, useEffect, useState } from "react";
import { portalApi, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Send, Plus, Inbox } from "lucide-react";

/** Buzón de la familia: escribe a su sede y responde a los mensajes del equipo. */
export const PortalMensajes = () => {
  const [hilos, setHilos] = useState([]);
  const [activo, setActivo] = useState(null);
  const [respuesta, setRespuesta] = useState("");
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ asunto: "", cuerpo: "" });
  const [enviando, setEnviando] = useState(false);

  const load = useCallback(() => {
    portalApi.get("/portal/mensajes").then(({ data }) => {
      setHilos(data);
      setActivo((prev) => (prev ? data.find((h) => h.id === prev.id) || null : null));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    setEnviando(true);
    try {
      await portalApi.post("/portal/mensajes", form);
      toast.success("Mensaje enviado a tu sede");
      setForm({ asunto: "", cuerpo: "" });
      setNuevo(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEnviando(false);
    }
  };

  const responder = async () => {
    if (!respuesta.trim()) return;
    try {
      await portalApi.post(`/portal/mensajes/${activo.id}/responder`, { cuerpo: respuesta });
      setRespuesta("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="portal-mensajes">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-extrabold">Buzón</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Escribe a la academia y consulta sus respuestas
          </p>
        </div>
        <Button className="rounded-full font-semibold" onClick={() => setNuevo((v) => !v)}
          data-testid="portal-nuevo-mensaje-btn">
          <Plus className="h-4 w-4 mr-2" /> Escribir
        </Button>
      </div>

      {nuevo && (
        <Card className="rounded-2xl p-6 mb-6 border-primary/40 bg-accent/30" data-testid="portal-nuevo-mensaje">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Asunto</Label>
              <Input value={form.asunto} data-testid="portal-mensaje-asunto"
                onChange={(e) => setForm({ ...form, asunto: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Mensaje</Label>
              <Textarea rows={5} value={form.cuerpo} data-testid="portal-mensaje-cuerpo"
                onChange={(e) => setForm({ ...form, cuerpo: e.target.value })} />
            </div>
            <Button className="rounded-full font-semibold justify-self-start" onClick={crear}
              disabled={enviando || form.asunto.trim().length < 2 || form.cuerpo.trim().length < 2}
              data-testid="portal-mensaje-enviar">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          {hilos.map((h) => (
            <Card key={h.id} onClick={() => setActivo(h)} data-testid={`portal-hilo-${h.id}`}
              className={`rounded-2xl p-5 border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-[2px] ${
                activo?.id === h.id ? "border-primary bg-accent/40" : "border-border"
              }`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-display font-bold text-sm">{h.asunto}</p>
                {h.no_leido && <Badge className="rounded-full text-[10px]">Nuevo</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{h.mensajes?.length || 1} mensaje(s)</p>
            </Card>
          ))}
          {hilos.length === 0 && (
            <Card className="rounded-2xl p-6 border-border" data-testid="portal-buzon-vacio">
              <Inbox className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">
                Todavía no tienes mensajes. Escribe a la academia cuando quieras.
              </p>
            </Card>
          )}
        </div>

        <Card className="rounded-2xl p-6 border-border shadow-sm lg:col-span-2" data-testid="portal-hilo-panel">
          {!activo && <p className="text-sm text-muted-foreground">Elige un mensaje para leerlo.</p>}
          {activo && (
            <>
              <h3 className="font-display text-xl font-bold mb-5">{activo.asunto}</h3>
              <div className="space-y-4">
                {(activo.mensajes || []).map((m, i) => (
                  <div key={i} className="rounded-xl border border-border p-4" data-testid={`portal-mensaje-${i}`}>
                    <p className="text-xs font-semibold">{m.autor?.nombre}
                      <span className="text-muted-foreground font-normal"> · {(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </p>
                    <p className="text-sm mt-2 whitespace-pre-wrap">{m.cuerpo}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid gap-3">
                <Textarea value={respuesta} data-testid="portal-respuesta-input"
                  placeholder="Escribe tu respuesta…" onChange={(e) => setRespuesta(e.target.value)} />
                <Button className="rounded-full font-semibold justify-self-start" onClick={responder}
                  data-testid="portal-responder-btn">
                  <Send className="h-4 w-4 mr-2" /> Responder
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
